import { dataParser } from "src/common/dataParser";
import { RtResponse } from "src/components/ReactiveTable/RtResponse";
import { AbstractRow } from "src/components/ReactiveTable/RtTypes";
import { z } from "zod";

/**
 * Тип для пагинации
 */
export const zPagination = z.object({
  totalElements: z.number(),
});
export type ZPagination = z.infer<typeof zPagination>;

/**
 * Базовая структура пагинированного ответа API
 */
export interface PaginationResponseData<T> {
  content: T[];
  pagination: ZPagination;
}

/**
 * Общий тип для пагинированных ответов API
 * @param T - тип элементов в массиве content
 *
 * @example
 * ```typescript
 * const zUsersResponse = createPaginationResponse(zUserRow);
 * const zRolesResponse = createPaginationResponse(zRole);
 * ```
 */
const createPaginationResponse = <T extends z.ZodTypeAny>(
  contentSchema: T,
): z.ZodType<PaginationResponseData<z.infer<T>>> =>
  z.object({
    content: contentSchema.array(),
    pagination: zPagination,
  });

/**
 * @deprecated Используйте mdmLoader вместо этой функции
 * Общая функция для обработки пагинированных ответов API
 * @param responseData - данные ответа от API (типизированы как ApiResponse)
 * @param dataSchema - схема Zod для валидации ответа
 * @param responseName - имя ответа для логирования ошибок
 * @param contentMapper - опциональная функция для преобразования элементов content
 * @returns объект RtResponse с rows и totalRows
 *
 * @example
 * ```typescript
 * // Без преобразования данных
 * return parsePaginationResponse(resp.data, zUsersResponse, "users response");
 *
 * // С преобразованием данных
 * return parsePaginationResponse(
 *   resp.data,
 *   zClsResponse,
 *   "classes response",
 *   (unsafeRow) => makeClassRow(unsafeRow, schemes)
 * );
 * ```
 */
export const parsePaginationResponse = <
  T extends AbstractRow,
  R extends AbstractRow = T,
>(
  responseData: unknown,
  dataSchema: z.ZodType<T>,
  responseName: string,
  contentMapper?: (item: T) => R,
): RtResponse<R> => {
  const zResponse = createPaginationResponse(dataSchema);
  const data = dataParser(() => zResponse.parse(responseData), responseName);
  return {
    rows: contentMapper
      ? data.content.map(contentMapper)
      : (data.content as unknown as R[]),
    totalRows: data.pagination.totalElements,
  };
};
